import { useAuthStore } from '@/shared/stores/auth'

const PREFIX = 'axiomgo-composer-v1:'
const listeners = new Set<() => void>()
interface Draft {
  text: string
  revision: string
  messageId?: string
  failed?: true
}
export interface DraftSubmission {
  key: string
  sourceKey?: string
  draft: Draft
}

export function composerKey(conversationId: string | null): string {
  const { tenantId, userId } = useAuthStore.getState()
  return tenantId && userId ? PREFIX + JSON.stringify([tenantId, userId, conversationId]) : ''
}

export function readComposerDraft(key: string): Draft | null {
  if (!key) return null
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? 'null')
    if (
      value &&
      typeof value === 'object' &&
      'text' in value &&
      'revision' in value &&
      typeof value.text === 'string' &&
      typeof value.revision === 'string'
    )
      return value as Draft
  } catch {
    /* Unavailable/corrupt cache must not prevent composition. */
  }
  return null
}

export function subscribeComposerDrafts(listener: () => void): () => void {
  listeners.add(listener)
  const unsubscribe = useAuthStore.subscribe?.(listener)
  return () => {
    listeners.delete(listener)
    unsubscribe?.()
  }
}

function write(key: string, draft: Draft | null): void {
  if (!key) return
  if (draft) localStorage.setItem(key, JSON.stringify(draft))
  else localStorage.removeItem(key)
  listeners.forEach((listener) => listener())
}

export function saveComposerDraft(key: string, text: string): void {
  write(key, text ? { text, revision: crypto.randomUUID() } : null)
}

export function captureComposerDraft(
  conversationId: string | null,
  text: string,
): DraftSubmission | null {
  const key = composerKey(conversationId)
  const draft = readComposerDraft(key)
  return draft?.text.trim() === text.trim() ? { key, draft } : null
}

// A newly created conversation takes ownership before the composer switches.
export function moveComposerDraft(
  submission: DraftSubmission | null,
  conversationId: string,
): DraftSubmission | null {
  if (!submission) return null
  const current = readComposerDraft(submission.key)
  if (!current) return null
  const key = composerKey(conversationId)
  if (!key || key === submission.key) return submission
  const owner = JSON.parse(submission.key.slice(PREFIX.length)) as string[]
  const { tenantId, userId } = useAuthStore.getState()
  if (owner[0] !== tenantId || owner[1] !== userId) return null
  try {
    write(key, current)
  } catch {
    return submission
  }
  // A failed remove may leave two copies; stage both with the same request id.
  try {
    write(submission.key, null)
  } catch {
    /* Keep the recoverable source. */
  }
  return { key, sourceKey: submission.key, draft: submission.draft }
}

export function stageComposerDraft(submission: DraftSubmission | null, messageId: string): boolean {
  const { tenantId, userId } = useAuthStore.getState()
  if (!tenantId || !userId) return false
  // No captured text means no persisted copy can resurface (e.g. attachment-only or chips).
  if (!submission) return true
  let prepared = true
  for (const key of [submission.key, submission.sourceKey]) {
    if (!key) continue
    if (!belongsTo(key, tenantId, userId)) return false
    if (readComposerDraft(key)?.revision !== submission.draft.revision) continue
    try {
      write(key, { text: submission.draft.text, revision: submission.draft.revision, messageId })
    } catch {
      prepared = false
    }
  }
  if (!prepared) restoreUnsentComposerDraft(submission, messageId)
  return prepared
}

/** A rejected dispatch restores only its captured owner and unchanged staged copy. */
export function restoreUnsentComposerDraft(
  submission: DraftSubmission | null,
  messageId: string,
): void {
  if (!submission) return
  const { tenantId, userId } = useAuthStore.getState()
  for (const key of [submission.key, submission.sourceKey]) {
    if (!key || !belongsTo(key, tenantId, userId)) continue
    const current = readComposerDraft(key)
    if (
      !current ||
      current.revision !== submission.draft.revision ||
      current.messageId !== messageId
    )
      continue
    try {
      write(key, { text: current.text, revision: current.revision })
    } catch {
      // A failed rollback keeps the staged original available for explicit recovery.
    }
  }
}

/** A REST action consumes only the unchanged unsent text captured for that action. */
export function consumeComposerDraft(submission: DraftSubmission | null): void {
  if (!submission) return
  const { tenantId, userId } = useAuthStore.getState()
  for (const key of [submission.key, submission.sourceKey]) {
    if (!key || !belongsTo(key, tenantId, userId)) continue
    const current = readComposerDraft(key)
    if (!current || current.revision !== submission.draft.revision || current.messageId) continue
    try {
      write(key, null)
    } catch {
      // A failed removal preserves the original rather than hiding an unconsumed copy.
    }
  }
}

/** Only a reported transport failure makes a staged request recoverable. */
export function failComposerDraft(
  messageId: string,
  origin?: { tenantId: string; userId: string },
): void {
  const { tenantId, userId } = useAuthStore.getState()
  if (origin && (origin.tenantId !== tenantId || origin.userId !== userId)) return
  try {
    for (const key of Object.keys(localStorage)) {
      if (!belongsTo(key, tenantId, userId)) continue
      const current = readComposerDraft(key)
      if (current?.messageId === messageId) write(key, { ...current, failed: true })
    }
  } catch {
    /* Cache failures must not interrupt the transport error handler. */
  }
}

export function confirmComposerDraft(messageId: string): void {
  // Inspect only the authenticated owner's drafts, never another identity's.
  const { tenantId, userId } = useAuthStore.getState()
  try {
    for (const key of Object.keys(localStorage)) {
      if (!belongsTo(key, tenantId, userId)) continue
      if (readComposerDraft(key)?.messageId === messageId) write(key, null)
    }
  } catch {
    /* A cache failure must not break processing the server response. */
  }
}

function belongsTo(key: string, tenantId: string, userId: string): boolean {
  if (!tenantId || !userId || !key.startsWith(PREFIX)) return false
  try {
    const owner: unknown = JSON.parse(key.slice(PREFIX.length))
    return Array.isArray(owner) && owner[0] === tenantId && owner[1] === userId
  } catch {
    return false
  }
}

export function clearComposerDrafts(tenantId: string, userId: string): void {
  for (const key of Object.keys(localStorage)) {
    if (belongsTo(key, tenantId, userId)) write(key, null)
  }
}

// Typed confirmation uses a separate existing transport path, with the same ACK rule.
export function confirmComposerAction(
  confirm: () => string | undefined | false,
  conversationId: string | null,
  text: string,
  clear: (submitted: string) => void,
): void {
  const submission = captureComposerDraft(conversationId, text)
  const messageId = confirm()
  if (!messageId) return
  stageComposerDraft(submission, messageId)
  clear(text)
}
